/**
 * Import a Nexus export: the zip that "Exporteren (Markdown, zip)" writes, from
 * Nexus or Nexus Open. Its properties are Nexus properties already, so unlike the
 * Obsidian import there is nothing to map: pages, folders, properties and
 * attachments come back as they were exported. The zip is only read.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, FileArchive } from 'lucide-react';
import {
  analyzeVault,
  runImport,
  unzipVault,
  type ImportProgress,
  type ImportResult,
  type VaultAnalysis,
} from '@nexus/workspace';
import { useStore } from '../db/context';
import { ModuleIcon } from './ModuleIcon';

type Step =
  | { kind: 'pick' }
  | { kind: 'reading' }
  | { kind: 'review'; analysis: VaultAnalysis; file: string }
  | { kind: 'running'; progress: ImportProgress }
  | { kind: 'done'; result: ImportResult }
  | { kind: 'error'; message: string };

export function NexusImportView() {
  const { t } = useTranslation();
  const store = useStore();
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [nested, setNested] = useState(true);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setStep({ kind: 'reading' });
    try {
      const analysis = await analyzeVault(unzipVault(new Uint8Array(await file.arrayBuffer())));
      if (analysis.notes.length === 0) throw new Error(t('nexusImport.empty'));
      setStep({ kind: 'review', analysis, file: file.name });
    } catch (e) {
      setStep({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  };

  const start = async (analysis: VaultAnalysis) => {
    setStep({ kind: 'running', progress: { phase: 'folders', done: 0, total: 1 } });
    try {
      const result = await runImport(
        store,
        analysis,
        {
          mapping: {}, // Nexus properties: kept as they are
          includeConflicts: true,
          rootTitle: nested ? t('nexusImport.rootTitle') : null,
          aiExcludedFolders: [],
          dailyFolder: null,
          templatesFolder: null,
          tasksFolder: null,
        },
        (progress) => setStep({ kind: 'running', progress }),
      );
      setStep({ kind: 'done', result });
    } catch (e) {
      setStep({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <section className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10">
      <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
        <ModuleIcon kind="import" size="lg" />
        {t('nexusImport.title')}
      </h1>
      <p className="mt-2 text-fg-muted">{t('nexusImport.intro')}</p>

      {(step.kind === 'pick' || step.kind === 'error') && (
        <div className="mt-8">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-accent px-4 py-2 font-medium text-accent-fg focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--focus)]">
            <FileArchive className="size-4" />
            {t('nexusImport.choose')}
            <input
              type="file"
              accept=".zip,application/zip"
              className="sr-only"
              aria-label={t('nexusImport.choose')}
              onChange={(e) => void pick(e.target.files?.[0])}
            />
          </label>
          <p className="mt-3 text-sm text-fg-muted">{t('nexusImport.backupHint')}</p>
          {step.kind === 'error' && (
            <p role="alert" className="mt-4 text-sm text-danger">
              {t('nexusImport.failed')}: {step.message}
            </p>
          )}
        </div>
      )}

      {step.kind === 'reading' && <p className="mt-8 text-fg-muted">{t('nexusImport.reading')}</p>}

      {step.kind === 'review' && (
        <div className="mt-8 flex flex-col gap-4">
          <div className="rounded-xl border border-border p-4">
            <p className="font-medium">{step.file}</p>
            <p className="mt-1 text-sm text-fg-muted">
              {t('nexusImport.summary', {
                pages: step.analysis.notes.length,
                attachments: step.analysis.attachments.length,
              })}
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={nested} onChange={(e) => setNested(e.target.checked)} />
            {t('nexusImport.nested', { title: t('nexusImport.rootTitle') })}
          </label>
          <p className="text-xs text-fg-muted">{t('nexusImport.nestedHint')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void start(step.analysis)}
              className="rounded-full bg-accent px-4 py-2 font-medium text-accent-fg"
            >
              {t('nexusImport.start')}
            </button>
            <button
              type="button"
              onClick={() => setStep({ kind: 'pick' })}
              className="rounded-full px-4 py-2 hover:bg-bg-hover"
            >
              {t('app.cancel')}
            </button>
          </div>
        </div>
      )}

      {step.kind === 'running' && (
        <div className="mt-8" role="status">
          <p className="mb-2 text-sm">
            {t(`import.phase.${step.progress.phase}`)} — {step.progress.done} /{' '}
            {step.progress.total}
          </p>
          <progress
            className="h-2 w-full accent-[var(--accent)]"
            value={step.progress.done}
            max={step.progress.total}
            aria-label={t('import.progress')}
          />
        </div>
      )}

      {step.kind === 'done' && (
        <div className="mt-8 rounded-xl border border-border p-4" role="status">
          <p className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="size-5 text-green-600" />
            {t('nexusImport.done', {
              pages: step.result.pages,
              attachments: step.result.attachments,
            })}
          </p>
        </div>
      )}
    </section>
  );
}
